/**
 * 框选矩形可视化插件 —— 在 Overlay2D 阶段绘制拖拽选区矩形。
 * 使用正交相机 + PlaneGeometry 在屏幕空间渲染半透明矩形和边框。
 */

import * as THREE from 'three'
import { WEBGL2_ONLY_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import { submitSceneInView } from '@/plugins/shared/submitSceneInView'
import {
  getActiveViewId,
  getActiveViewRect,
  getOptionalViewRegistry,
  getViewOverlayRoot,
} from '@/plugins/tools/shared/viewScope'
import type { EngineContext } from '@/runtime/EngineContext'
import type { PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type { Plugin } from '@/shared/types/plugin'
import type {
  RenderContributor,
  RenderPipeline,
  ScreenBoxSelectService,
  ViewRegistry,
} from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'

/** 选区填充颜色 */
const FILL_COLOR = 0x4488ff
/** 选区填充透明度 */
const FILL_OPACITY = 0.15
/** 选区边框颜色 */
const BORDER_COLOR = 0xffffff

/**
 * 框选矩形可视化插件
 */
export class ScreenBoxSelectOverlayPlugin implements Plugin, RenderContributor {
  /** 插件名称 */
  readonly name = 'screenBoxSelect-overlay'
  readonly graphicsRequirements = WEBGL2_ONLY_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级 */
  readonly priority = 56
  /** 依赖的服务名列表 */
  readonly dependencies = ['ScreenBoxSelectService', 'RenderPipeline'] as const

  // RenderContributor
  /** 渲染贡献者类型标识 */
  readonly kind = 'screenBoxSelect-overlay'
  /** 需要参与的渲染阶段 */
  readonly phases = [RenderPhase.Overlay2D] as const
  /** 渲染优先级 */
  readonly renderPriority = 0

  /** 引擎上下文 */
  private context!: EngineContext

  /** 框选服务 */
  private selectService!: ScreenBoxSelectService

  /** 渲染管线 */
  private pipeline!: RenderPipeline

  /** 2D 叠加层场景 */
  private readonly overlayScene = new THREE.Scene()

  /** 常驻 root，空闲时挂回当前 view 的 overlayRoot */
  private readonly root = new THREE.Group()

  /** 屏幕空间正交相机 */
  private readonly overlayCamera = new THREE.OrthographicCamera(0, 1, 1, 0, -1, 1)

  /** 选区填充 Mesh */
  private fillMesh: THREE.Mesh | null = null

  /** 选区边框 Line */
  // WebGPU 不支持 LineLoop 图元：用闭合的 LineSegments 表达同一个矩形边框。
  private borderLine: THREE.LineSegments | null = null

  /** 填充材质 */
  private readonly fillMaterial = new THREE.MeshBasicMaterial({
    color: FILL_COLOR,
    transparent: true,
    opacity: FILL_OPACITY,
    depthTest: false,
    depthWrite: false,
    side: THREE.DoubleSide,
  })

  /** 边框材质 */
  private readonly borderMaterial = new THREE.LineBasicMaterial({
    color: BORDER_COLOR,
    depthTest: false,
    depthWrite: false,
  })

  private viewRegistry: ViewRegistry | null = null

  // ─── Plugin 生命周期 ───

  /**
   * 初始化：保存上下文。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
  }

  /** 启动：获取服务，注册 RenderContributor */
  onStart(): void {
    this.selectService = this.context.getService('ScreenBoxSelectService')
    this.pipeline = this.context.getService('RenderPipeline')
    this.viewRegistry = getOptionalViewRegistry(this.context)
    this.pipeline.addRenderContributor(this)
    this.root.name = 'screenBoxSelectOverlayRoot'
    this.resolveOverlayRoot(getActiveViewId(this.viewRegistry)).add(this.root)
  }

  /** 销毁：清理 Three.js 资源 */
  onDestroy(): void {
    this.pipeline.removeRenderContributor(this)
    this.disposeOverlayObjects()
    this.fillMaterial.dispose()
    this.borderMaterial.dispose()
    this.root.removeFromParent()
    this.viewRegistry = null
  }

  // ─── RenderContributor ───

  /**
   * 渲染框选叠加层。
   *
   * @param _frame - 帧上下文
   * @param _phase - 当前渲染阶段
   * @param ctx - 当前阶段渲染上下文，用于取得视口矩形
   */
  render(_frame: ViewFrameContext, _phase: RenderPhase, ctx: PhaseRenderContext): void {
    const activeViewId = getActiveViewId(this.viewRegistry)
    if (activeViewId && ctx.viewId !== activeViewId) {
      return
    }

    const overlayRoot = this.resolveOverlayRoot(activeViewId ?? ctx.viewId)
    if (this.root.parent !== overlayRoot) {
      this.root.parent?.remove(this.root)
      overlayRoot.add(this.root)
    }

    const rect = this.selectService.getCurrentRect()

    if (!rect) {
      // 无框选时清除已有叠加对象
      if (this.fillMesh) {
        this.disposeOverlayObjects()
      }
      return
    }

    const viewRect = getActiveViewRect(this.viewRegistry) ?? ctx.viewRect
    const { width, height } = viewRect
    if (width === 0 || height === 0) return

    this.overlayCamera.left = 0
    this.overlayCamera.right = width
    this.overlayCamera.top = 0
    this.overlayCamera.bottom = height
    this.overlayCamera.updateProjectionMatrix()

    const left = Math.min(rect.start.x, rect.end.x) - viewRect.x
    const top = Math.min(rect.start.y, rect.end.y) - viewRect.y
    const rectWidth = Math.abs(rect.end.x - rect.start.x)
    const rectHeight = Math.abs(rect.end.y - rect.start.y)

    // 矩形中心
    const cx = left + rectWidth / 2
    const cy = top + rectHeight / 2

    // 创建或更新填充 Mesh
    this.ensureOverlayObjects()

    if (this.fillMesh) {
      this.fillMesh.scale.set(rectWidth, rectHeight, 1)
      this.fillMesh.position.set(cx, cy, 0)
    }

    if (this.borderLine) {
      const positions = this.borderLine.geometry.getAttribute('position')
      if (positions) {
        // 四个角点（屏幕坐标），第 5 点闭合矩形
        positions.setXYZ(0, left, top, 0)
        positions.setXYZ(1, left + rectWidth, top, 0)
        positions.setXYZ(2, left + rectWidth, top + rectHeight, 0)
        positions.setXYZ(3, left, top + rectHeight, 0)
        positions.setXYZ(4, left, top, 0)
        positions.needsUpdate = true
      }
    }

    if (this.root.parent) {
      this.root.parent.remove(this.root)
    }
    this.overlayScene.add(this.root)
    submitSceneInView(ctx, this.overlayScene, this.overlayCamera)
    this.overlayScene.remove(this.root)
    overlayRoot.add(this.root)
  }

  // ─── 私有方法 ───

  /** 创建叠加层几何体（延迟创建） */
  private ensureOverlayObjects(): void {
    if (this.fillMesh) return

    const fillGeometry = new THREE.PlaneGeometry(1, 1)
    this.fillMesh = new THREE.Mesh(fillGeometry, this.fillMaterial)
    this.root.add(this.fillMesh)

    const borderGeometry = new THREE.BufferGeometry()
    // 5 个顶点把最后一段闭合回起点（矩形 4 角 + 重复首点）
    const borderPositions = new Float32Array(5 * 3)
    borderGeometry.setAttribute('position', new THREE.BufferAttribute(borderPositions, 3))
    this.borderLine = new THREE.LineSegments(borderGeometry, this.borderMaterial)
    this.root.add(this.borderLine)
  }

  /** 释放叠加层对象 */
  private disposeOverlayObjects(): void {
    if (this.fillMesh) {
      this.fillMesh.geometry.dispose()
      this.root.remove(this.fillMesh)
      this.fillMesh = null
    }
    if (this.borderLine) {
      this.borderLine.geometry.dispose()
      this.root.remove(this.borderLine)
      this.borderLine = null
    }
  }

  private resolveOverlayRoot(viewId: string | null): THREE.Object3D {
    return getViewOverlayRoot(this.context, this.viewRegistry, viewId)
  }
}
